import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";

import { cn } from "@/lib/utils";
import { useReducedMotion } from "@/hooks/use-reduced-motion";
import { ModalStackProvider } from "@/lib/modal-stack";

function Popover({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />;
}

function PopoverTrigger({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />;
}

// Popover is at z-index 1100. To ensure dialogs opened from within a popover
// render above it, we provide a ModalStackProvider with a depth that will
// result in z-index > 1100. With Z_INDEX_MODAL_CONTENT=1060 and Z_INDEX_STEP=10,
// depth 5 gives: 1060 + (5 * 10) = 1110, which is above 1100.
const POPOVER_EQUIVALENT_DEPTH = 4;

// Open/close animation. `scale` is the long-standing zoom-and-slide; `rise` is
// the quieter fade-and-lift used by the selector menus, which open next to a
// toolbar control where a zoom reads as a pop. Both stop dead under
// prefers-reduced-motion.
const POPOVER_MOTION_CLASSES = {
  scale:
    "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:fill-mode-forwards data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 motion-reduce:animate-none",
  rise: "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:slide-in-from-bottom-1 data-[state=open]:zoom-in-[0.98] data-[state=closed]:zoom-out-[0.98] data-[state=closed]:fill-mode-forwards duration-[120ms] ease-out motion-reduce:animate-none",
  none: "",
} as const;

export type PopoverMotion = keyof typeof POPOVER_MOTION_CLASSES;

function PopoverContent({
  className,
  align = "center",
  sideOffset = 4,
  children,
  disableAnimation = false,
  motion,
  widthMode = "default",
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content> & {
  disableAnimation?: boolean;
  motion?: PopoverMotion;
  // `content`: the panel sizes to its own contents rather than the trigger, so
  // a narrow trigger cannot squeeze its own menu. Bound by min/max via style.
  widthMode?: "default" | "trigger" | "content" | "custom";
}) {
  // `disableAnimation` is the older accessibility switch and still wins. The
  // preference is read here rather than left to `motion-reduce:`, which cannot
  // outrank the entrance utilities. See `useReducedMotion`.
  const prefersReducedMotion = useReducedMotion();
  const resolvedMotion: PopoverMotion =
    disableAnimation || prefersReducedMotion ? "none" : (motion ?? "scale");
  const content = (
    <PopoverPrimitive.Content
      data-slot="popover-content"
      align={align}
      sideOffset={sideOffset}
      className={cn(
        // z-[1100]: above modals (z-1050–1060+) so portaled popovers are interactive. See z-index.ts for layer map.
        "text-xs leading-normal font-semibold bg-white text-neutral-900 z-[1100] origin-(--radix-popover-content-transform-origin) rounded-md border border-neutral-100 p-4 shadow-md outline-hidden",
        widthMode === "default" && "w-72",
        // Utilities are `!important` in this build, so an inline width loses.
        // A floor arrives as `--selector-menu-floor` (see useTriggerWidth).
        widthMode === "trigger" &&
          "w-[min(max(var(--radix-popover-trigger-width),var(--selector-menu-floor,0px)),calc(100vw-2rem))] min-w-[min(max(var(--radix-popover-trigger-width),var(--selector-menu-floor,0px)),calc(100vw-2rem))]",
        widthMode === "content" && "w-max max-w-[calc(100vw-16px)]",
        POPOVER_MOTION_CLASSES[resolvedMotion],
        className
      )}
      // Prevent scroll events from propagating to react-remove-scroll (used by modals),
      // which blocks wheel/touch interactions on portaled content outside the modal.
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
      {...props}
    >
      <ModalStackProvider parentDepth={POPOVER_EQUIVALENT_DEPTH}>
        {children}
      </ModalStackProvider>
    </PopoverPrimitive.Content>
  );

  // Skip portal in test environment for better testability
  if (process.env.NODE_ENV === "test") {
    return content;
  }

  return <PopoverPrimitive.Portal>{content}</PopoverPrimitive.Portal>;
}

function PopoverAnchor({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
  return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />;
}

function PopoverClose({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Close>) {
  return <PopoverPrimitive.Close data-slot="popover-close" {...props} />;
}

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor, PopoverClose };
